import apiV2 from '../api/v2';
import {
    statusQuery,
    type DmInboxPatientFields,
    type DmInboxStatus,
    type DmListPosition,
    type DmNotificationAssignee,
    type DmPatientVersion
} from './dmNotifications';

export type DmMessageInboxSort = 'latest' | 'urgency' | 'frustration' | 'waiting';
export type DmMessageInboxOrder = 'asc' | 'desc';

export type DmMessageUrgency = 'normal' | 'high' | 'extreme';
// null: nobody has rated the patient's frustration yet.
export type DmMessageFrustration = 'low' | 'medium' | 'high' | null;

export interface DmMessageRowTriage {
    urgency: DmMessageUrgency;
    frustration: DmMessageFrustration;
    hasSuggestedReply: boolean;
    // null: the AI hasn't triaged this conversation.
    replyNeeded: boolean | null;
    urgencyByAi: boolean;
    frustrationByAi: boolean;
}

export type DmTriageSetter = DmNotificationAssignee;

export interface DmMessagePatientTriage {
    urgency: DmMessageUrgency;
    frustration: DmMessageFrustration;
    suggestedReply: string | null;
    suggestedReplyGeneratedAt: string | null;
    urgencySetBy: DmTriageSetter | null;
    urgencySetAt: string | null;
    frustrationSetBy: DmTriageSetter | null;
    frustrationSetAt: string | null;
    replyNeeded: boolean | null;
    // Title of the quick reply the AI based its draft on.
    basedOnQuickReply: string | null;
    // receivedAt of the newest patient message the AI had read.
    aiBasisMessageAt: string | null;
    aiTriagedAt: string | null;
    urgencyByAi: boolean;
    frustrationByAi: boolean;
}

// Omitted fields stay as they are; frustration null clears it.
export interface DmMessageTriagePatch {
    urgency?: DmMessageUrgency;
    frustration?: DmMessageFrustration;
}

export interface DmInboxMessage {
    id: string;
    dmEventId: string;
    dmMessageId: string | null;
    text: string | null;
    receivedAt: string;
}

export interface DmMessagePatientRow extends DmInboxPatientFields {
    patientId: string;
    patientName: string;
    practiceId: string | null;
    practiceName: string | null;
    doctorName: string | null;
    profileThumbnailUrl: string | null;
    version: string | null;
    // Shared with the notifications list: one assignee per patient.
    assignee: DmNotificationAssignee | null;
    unreviewedCount: number;
    latestAt: string;
    // receivedAt of the oldest unreviewed message.
    oldestAt: string;
    latestMessage: DmInboxMessage;
    triage: DmMessageRowTriage;
}

export interface DmMessageInboxMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    totalMessages: number;
}

export interface DmMessageInboxPage {
    data: DmMessagePatientRow[];
    meta: DmMessageInboxMeta;
}

export interface DmMessageInboxFilters {
    sort: DmMessageInboxSort;
    order: DmMessageInboxOrder;
    search?: string;
    practiceIds?: string[];
    // Emails, plus 'null' for unassigned; matched with OR.
    assignees?: string[];
    versions?: DmPatientVersion[];
    status?: DmInboxStatus;
}

export interface DmMessageInboxParams extends DmMessageInboxFilters {
    page: number;
    limit: number;
}

const INBOX_PATH = '/admin/monitoring/messages';

function filterQuery(filters: DmMessageInboxFilters): Record<string, string | string[]> {
    const query: Record<string, string | string[]> = {
        sort: filters.sort,
        order: filters.order
    };
    if (filters.search?.trim()) query.search = filters.search.trim();
    if (filters.practiceIds?.length) query.practiceIds = filters.practiceIds;
    if (filters.assignees?.length) query.assignees = filters.assignees;
    if (filters.versions?.length) query.versions = filters.versions;
    return { ...query, ...statusQuery(filters.status ?? 'open') };
}

export const fetchDmMessageInbox = async (
    params: DmMessageInboxParams
): Promise<DmMessageInboxPage> => {
    const query = {
        ...filterQuery(params),
        page: String(params.page),
        limit: String(params.limit)
    };
    const response = await apiV2.get<{ success: boolean } & DmMessageInboxPage>(INBOX_PATH, {
        params: query
    });
    return { data: response.data.data, meta: response.data.meta };
};

// latestAt, oldestAt and latestMessage are null once every message has been reviewed.
export interface DmMessagePatientDetail extends Omit<
    DmMessagePatientRow,
    'latestAt' | 'oldestAt' | 'latestMessage' | 'triage'
> {
    latestAt: string | null;
    oldestAt: string | null;
    latestMessage: DmInboxMessage | null;
    triage: DmMessagePatientTriage;
}

export interface DmMessagePatientView {
    patient: DmMessagePatientDetail;
    // Neighbours within the list's filters; null at either end or once the patient drops out.
    previousPatientId: string | null;
    nextPatientId: string | null;
    // Older servers omit it.
    listPosition?: DmListPosition;
}

export const fetchDmMessagePatient = async (
    patientId: string,
    filters: DmMessageInboxFilters
): Promise<DmMessagePatientView> => {
    const response = await apiV2.get<{ success: boolean; data: DmMessagePatientView }>(
        `${INBOX_PATH}/patients/${encodeURIComponent(patientId)}`,
        { params: filterQuery(filters) }
    );
    return response.data.data;
};

export interface DmMessageInboxCount {
    total: number;
    patients: number;
}

// Counts of the patient's messages; `failed` ones stay unreviewed.
export interface DmPatientMessagesReviewResult {
    reviewed: number;
    reviewedInDm: number;
    alreadyResolvedInDm: number;
    failed: number;
}

export const reviewDmPatientMessages = async (
    patientId: string
): Promise<DmPatientMessagesReviewResult> => {
    const response = await apiV2.post<{ success: boolean; data: DmPatientMessagesReviewResult }>(
        `${INBOX_PATH}/patients/${encodeURIComponent(patientId)}/review`
    );
    return response.data.data;
};

export const patchDmMessageTriage = async (
    patientId: string,
    patch: DmMessageTriagePatch
): Promise<DmMessagePatientTriage> => {
    const response = await apiV2.patch<{
        success: boolean;
        data: { triage: DmMessagePatientTriage };
    }>(`${INBOX_PATH}/patients/${encodeURIComponent(patientId)}/triage`, patch);
    return response.data.data.triage;
};
